import {
  IReportIntrospection,
  ReportFilterEntry,
  ReportFilterEntryIntrospection,
} from '@common/types';
import { selectBooleanOptions } from '@common/types/constants.ts';
import { TextField, InputAdornment, Tooltip } from '@mui/material';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { MultiselectSearchableField } from '../../../../../../../../../components/Multiselect';
import dayjs from 'dayjs';
import { startCase } from 'lodash';
import { Select } from '@ui';
import { operatorLabels } from '../../../types.ts';
import { useLocation } from 'react-router-dom';
import {
  useBuildingsShortQuery,
  useFilterValuesQuery,
  useOperatorShortQuery,
  useOrganizationsShortQuery,
  usePartnerShortQuery,
  usePracticeShortQuery,
  useProvidersShortQuery,
  useTeamsShortQuery,
  useUserListShortQuery,
} from '@common/cache';
import { useReportStore } from 'store';

interface IFilterTypeMapper {
  entry?: ReportFilterEntry;
  introspection?: ReportFilterEntryIntrospection;
  handleOnChange: (newValue: ReportFilterEntry['value']) => void;
}

const useShowAdornment = () => {
  const location = useLocation();
  return (
    location.pathname.includes('/preview') ||
    location.pathname.includes('/items')
  );
};

const getStartAdornment = (operator?: string) => {
  return operator ? (
    <Tooltip
      title={
        operatorLabels[
          operator as IReportIntrospection['filters'][0]['operators'][0]
        ]
      }
      placement='top'
    >
      <InputAdornment
        position='start'
        sx={{ pointerEvents: 'none', flexShrink: 0, maxWidth: '120px' }}
      >
        <span
          style={{
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            display: 'block',
            maxWidth: '120px',
          }}
        >
          {
            operatorLabels[
              operator as IReportIntrospection['filters'][0]['operators'][0]
            ]
          }
        </span>
      </InputAdornment>
    </Tooltip>
  ) : undefined;
};

export const FilterTypeMapper = (props: IFilterTypeMapper) => {
  const { introspection, entry } = props;
  const showAdornment = useShowAdornment();

  if (entry?.operator === '$isnull') {
    return <BooleanFilter {...props} showAdornment={showAdornment} />;
  }

  switch (introspection?.type) {
    case 'enum':
      if (entry?.operator === '$in' || entry?.operator === '$nin')
        return <MultiselectFilter {...props} showAdornment={showAdornment} />;
      else return <SelectFilter {...props} showAdornment={showAdornment} />;
    case 'boolean':
      return <BooleanFilter {...props} showAdornment={showAdornment} />;
    case 'date':
      return <DateFilter {...props} showAdornment={showAdornment} />;
    case 'partner':
      return <PartnerSearchFilter {...props} showAdornment={showAdornment} />;
    case 'organization':
      return (
        <OrganizationSearchFilter {...props} showAdornment={showAdornment} />
      );
    case 'user':
      return <UserSearchFilter {...props} showAdornment={showAdornment} />;
    case 'operator':
      return <OperatorSearchFilter {...props} showAdornment={showAdornment} />;
    case 'building':
      return <BuildingSearchFilter {...props} showAdornment={showAdornment} />;
    case 'team':
      return <TeamSearchFilter {...props} showAdornment={showAdornment} />;
    case 'provider':
      return <ProviderSearchFilter {...props} showAdornment={showAdornment} />;
    case 'practice':
      return <PracticeSearchFilter {...props} showAdornment={showAdornment} />;
    case 'text':
      if (entry?.operator === '$in' || entry?.operator === '$nin')
        return (
          <TextMultiselectFilter {...props} showAdornment={showAdornment} />
        );
      else return <TextFilter {...props} showAdornment={showAdornment} />;
    default:
      return <TextFilter {...props} showAdornment={showAdornment} />;
  }
};

const TextFilter = ({
  entry,
  handleOnChange,
  showAdornment,
}: IFilterTypeMapper & { showAdornment: boolean }) => (
  <TextField
    disabled={!entry?.field}
    value={entry?.value ?? ''}
    name={entry?.field}
    label={entry?.label}
    onChange={event => handleOnChange(event.target.value)}
    fullWidth
    slotProps={{
      input: {
        startAdornment: showAdornment
          ? getStartAdornment(entry?.operator)
          : null,
      },
    }}
  />
);

const SelectFilter = ({
  entry,
  introspection,
  handleOnChange,
  showAdornment,
}: IFilterTypeMapper & { showAdornment: boolean }) =>
  introspection?.type === 'enum' ? (
    <Select
      required={false}
      options={introspection.enum_options.map(o => ({
        value: o,
        label: startCase(o),
      }))}
      value={(entry?.value as string) ?? ''}
      name={entry?.field ?? introspection?.field ?? ''}
      label={entry?.label}
      onChange={event => handleOnChange(event.target.value)}
      disabled={!entry?.field}
      startAdornment={
        showAdornment ? getStartAdornment(entry?.operator) : undefined
      }
    />
  ) : null;

const MultiselectFilter = ({
  entry,
  introspection,
  handleOnChange,
  showAdornment,
}: IFilterTypeMapper & { showAdornment: boolean }) =>
  introspection?.type === 'enum' ? (
    <MultiselectSearchableField
      optional
      options={introspection.enum_options.map(o => ({
        value: o,
        label: startCase(o),
      }))}
      value={Array.isArray(entry?.value) ? entry?.value : []}
      name={entry?.field ?? introspection?.field ?? ''}
      label={entry?.label}
      handleChange={values => handleOnChange(values)}
      disabled={!entry?.field}
      startAdornment={
        showAdornment ? getStartAdornment(entry?.operator) : undefined
      }
    />
  ) : null;

type ReportType =
  'patients_report' | 'tasks_report' | 'users_report' | 'records_report';

type ReportSegment = 'patients' | 'users' | 'tasks' | 'record';

const reportTypeToSegment: Record<ReportType, ReportSegment> = {
  patients_report: 'patients',
  tasks_report: 'tasks',
  users_report: 'users',
  records_report: 'record',
};

const TextMultiselectFilter = ({
  entry,
  introspection,
  handleOnChange,
  showAdornment,
}: IFilterTypeMapper & { showAdornment: boolean }) => {
  const { report } = useReportStore();
  const values = Array.isArray(entry?.value) ? entry?.value : [];

  const segment = report?.report_type
    ? reportTypeToSegment[report.report_type as ReportType]
    : 'patients';

  const { data: options = [] } = useFilterValuesQuery({
    field: entry?.field || '',
    segment,
  });

  return (
    <MultiselectSearchableField
      optional
      options={options.map(v => ({ value: v, label: v }))}
      value={values}
      name={entry?.field ?? introspection?.field ?? ''}
      label={entry?.label}
      handleChange={newValues => handleOnChange(newValues)}
      disabled={!entry?.field}
      minOptionMenuWidth='300px'
      startAdornment={
        showAdornment ? getStartAdornment(entry?.operator) : undefined
      }
    />
  );
};

const BooleanFilter = ({
  entry,
  introspection,
  handleOnChange,
  showAdornment,
}: IFilterTypeMapper & { showAdornment: boolean }) => {
  const stringifiedValue = entry?.value.toString();

  return (
    <Select
      required={false}
      options={selectBooleanOptions}
      value={stringifiedValue}
      name={entry?.field ?? introspection?.field ?? ''}
      label={entry?.label}
      onChange={event => handleOnChange(event.target.value)}
      disabled={!entry?.field}
      startAdornment={
        showAdornment ? getStartAdornment(entry?.operator) : undefined
      }
    />
  );
};

const DateFilter = ({
  entry,
  handleOnChange,
  showAdornment,
}: IFilterTypeMapper & { showAdornment: boolean }) => {
  const dayjsValue = dayjs(entry?.value as string);
  return (
    <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale='en'>
      <DatePicker
        label={entry?.label}
        value={dayjsValue.isValid() && !!entry?.value ? dayjsValue : null}
        onChange={val => {
          const formattedValue = val?.isValid()
            ? dayjs(val).format('YYYY-MM-DD')
            : '';
          handleOnChange(formattedValue);
        }}
        disabled={!entry?.field}
        slotProps={{
          textField: {
            fullWidth: true,
            slotProps: {
              input: {
                startAdornment: showAdornment
                  ? getStartAdornment(entry?.operator)
                  : undefined,
              },
            },
          },
        }}
      />
    </LocalizationProvider>
  );
};

interface AsyncSearchFilterProps extends IFilterTypeMapper {
  showAdornment: boolean;
  options: { id: string; name: string }[];
  isLoading?: boolean;
}

const AsyncSearchFilter = ({
  entry,
  introspection,
  handleOnChange,
  showAdornment,
  options,
  isLoading,
}: AsyncSearchFilterProps) => {
  const mappedOptions = options.map(o => ({
    value: o.id,
    label: o.name,
  }));

  const isMulti = entry?.operator === '$in' || entry?.operator === '$nin';

  if (isMulti) {
    return (
      <MultiselectSearchableField
        optional
        options={mappedOptions}
        value={Array.isArray(entry?.value) ? entry?.value : []}
        name={entry?.field ?? introspection?.field ?? ''}
        label={entry?.label}
        handleChange={values => handleOnChange(values)}
        disabled={!entry?.field || isLoading}
        minOptionMenuWidth='300px'
        startAdornment={
          showAdornment ? getStartAdornment(entry?.operator) : undefined
        }
      />
    );
  }

  return (
    <Select
      required={false}
      options={mappedOptions}
      value={(entry?.value as string) ?? ''}
      name={entry?.field ?? introspection?.field ?? ''}
      label={entry?.label}
      onChange={event => handleOnChange(event.target.value)}
      disabled={!entry?.field || isLoading}
      startAdornment={
        showAdornment ? getStartAdornment(entry?.operator) : undefined
      }
    />
  );
};

const PartnerSearchFilter = (
  props: IFilterTypeMapper & { showAdornment: boolean },
) => {
  const { introspection } = props;
  const { data: optionsRaw = [], isLoading } = usePartnerShortQuery();

  return (
    <AsyncSearchFilter
      {...props}
      introspection={introspection}
      options={optionsRaw}
      isLoading={isLoading}
    />
  );
};

const OrganizationSearchFilter = (
  props: IFilterTypeMapper & { showAdornment: boolean },
) => {
  const { introspection } = props;
  const { data: optionsRaw = [], isLoading } = useOrganizationsShortQuery();

  return (
    <AsyncSearchFilter
      {...props}
      introspection={introspection}
      options={optionsRaw}
      isLoading={isLoading}
    />
  );
};

const OperatorSearchFilter = (
  props: IFilterTypeMapper & { showAdornment: boolean },
) => {
  const { introspection } = props;
  const { data: optionsRaw = [], isLoading } = useOperatorShortQuery();

  return (
    <AsyncSearchFilter
      {...props}
      introspection={introspection}
      options={optionsRaw}
      isLoading={isLoading}
    />
  );
};

const TeamSearchFilter = (
  props: IFilterTypeMapper & { showAdornment: boolean },
) => {
  const { introspection } = props;
  const { data: optionsRaw = [], isLoading } = useTeamsShortQuery({});

  return (
    <AsyncSearchFilter
      {...props}
      introspection={introspection}
      options={optionsRaw}
      isLoading={isLoading}
    />
  );
};

const UserSearchFilter = (
  props: IFilterTypeMapper & { showAdornment: boolean },
) => {
  const { introspection } = props;
  const { data: optionsRaw = [], isLoading } = useUserListShortQuery({});

  return (
    <AsyncSearchFilter
      {...props}
      introspection={introspection}
      options={optionsRaw}
      isLoading={isLoading}
    />
  );
};

const ProviderSearchFilter = (
  props: IFilterTypeMapper & { showAdornment: boolean },
) => {
  const { introspection } = props;
  const { data: optionsRaw = [], isLoading } = useProvidersShortQuery({});

  return (
    <AsyncSearchFilter
      {...props}
      introspection={introspection}
      options={optionsRaw}
      isLoading={isLoading}
    />
  );
};

const PracticeSearchFilter = (
  props: IFilterTypeMapper & { showAdornment: boolean },
) => {
  const { introspection } = props;
  const { data: optionsRaw = [], isLoading } = usePracticeShortQuery({});

  return (
    <AsyncSearchFilter
      {...props}
      introspection={introspection}
      options={optionsRaw}
      isLoading={isLoading}
    />
  );
};

const BuildingSearchFilter = (
  props: IFilterTypeMapper & { showAdornment: boolean },
) => {
  const { introspection } = props;
  const { data: optionsRaw = [], isLoading } = useBuildingsShortQuery({});

  return (
    <AsyncSearchFilter
      {...props}
      introspection={introspection}
      options={optionsRaw}
      isLoading={isLoading}
    />
  );
};
